import React from 'react';
import { ChevronRight, Info } from 'lucide-react';
import type { Product } from '../../pages/ProductMatrixNew';
import { sortByAvailability } from '../../utils/tableHelpers';
import { sortPaymentMethods } from '../../utils/paymentHelpers';

interface Country {
  code: string;
  name: string;
  flag: string;
}

interface Level2PlatformTableSectionProps {
  platformName: string;
  platformData: { platform_description: string; products: Product[] };
  visibleCountries: Country[];
  includeHeader?: boolean;
  browseMode: boolean;
  selectedCountry: string | null;
  selectedPlatform: string;
  onProductClick: (product: Product) => void;
  countries: Country[];
  highlightedProduct?: string | null;
}

/**
 * Level2PlatformTableSection Component
 *
 * Displays payment methods and other Level 2 platform products in a table format.
 *
 * NOTE: This component ONLY operates in browse mode (showing all visible countries).
 * Legacy "location-filtered mode" code was removed in January 2025.
 * If country filtering is needed in the future, it will need to be rebuilt.
 *
 * The browseMode and selectedCountry props are kept for backward compatibility
 * but are not actively used for filtering behavior.
 */
export const Level2PlatformTableSection: React.FC<Level2PlatformTableSectionProps> = ({
  platformName,
  platformData,
  visibleCountries,
  includeHeader = false,
  browseMode,
  selectedCountry,
  selectedPlatform,
  onProductClick,
  countries,
  highlightedProduct
}) => {
  // Defensive check: return null if platformData is undefined or has no products
  if (!platformData || !platformData.products || platformData.products.length === 0) {
    return null;
  }

  // Filter products that have availability in at least one of the shown countries
  const filteredProducts = platformData.products.filter(product => {
    // Check if product is available in any of the visible countries
    return visibleCountries.some(country => product.countries[country.code] === true);
  });

  // Return null if no products pass the filter
  if (filteredProducts.length === 0) {
    return null;
  }

  // Sort products: use custom payment order for Supported Payments and Shift4 Payments tabs, otherwise sort by availability
  const sortedProducts = (platformName === 'Supported Payments' || platformName === 'Shift4 Payments')
    ? sortPaymentMethods(filteredProducts)
    : sortByAvailability(filteredProducts);

  // For integrations, we remove both Tag and Status columns to save space
  const columnWidth = visibleCountries.length >= 14 ? '45px' : '60px';

  // Level 2 platforms always show table view (not cards) to ensure headers are visible

  return (
    <>
      {/* Mobile Card View - Hidden for Level 2 platforms to ensure headers always show */}
      <div className="hidden space-y-4">
        {sortedProducts.map((product, index) => {
          const isHighlighted = highlightedProduct && product.solution === highlightedProduct;
          return (
          <div
            key={index}
            onClick={() => onProductClick(product)}
            className={`bg-white border border-border rounded-lg p-4 shadow-sm cursor-pointer hover:shadow-md transition-shadow ${
              isHighlighted ? 'bg-yellow-100 animate-pulse' : ''
            }`}
            data-product-name={product.solution}
          >
            <div className="flex items-center gap-2 font-semibold text-foreground text-base mb-3 pb-2 border-b border-border">
              <span>{product.solution}</span>
              {product.notes && (
                <div className="flex-shrink-0" title="Additional details available">
                  <Info className="w-4 h-4 text-blue-500" />
                </div>
              )}
            </div>
            <div className="grid grid-cols-3 gap-2">
              {visibleCountries.map(country => {
                const value = product.countries[country.code];
                const isSupportedPaymentsTab = platformName === 'Supported Payments';
                const isSkyTabPlatform = selectedPlatform === 'SkyTab' || selectedPlatform === 'SkyTab Venue';
                const isSpainOrAustralia = country.code === 'Spain' || country.code === 'Australia';
                const shouldShowBlank = isSupportedPaymentsTab && isSkyTabPlatform && isSpainOrAustralia;

                return (
                  <div key={country.code} className="flex flex-col items-center justify-center p-2 bg-gray-50 rounded">
                    <span className="text-xs text-muted-foreground mb-1">{country.flag}</span>
                    <span className="text-xs font-medium text-muted-foreground">{country.code}</span>
                    <span className="mt-1">
                      {shouldShowBlank ? (
                        <span className="text-transparent">-</span>
                      ) : value === true ? (
                        <span className="text-green-600 font-semibold">✓</span>
                      ) : value === 'Coming Soon' ? (
                        <span className="text-xs italic text-muted-foreground">Soon</span>
                      ) : (
                        <span className="text-red-500 font-semibold">✗</span>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        );
        })}
      </div>

      {/* Table View - Always visible for Level 2 platforms to ensure headers show */}
      <div className="block bg-card border border-border rounded-lg overflow-x-auto lg:overflow-x-visible">
        {/* Table */}
        <table className="w-full min-w-[600px]" style={{tableLayout: 'fixed'}}>
          <thead className={includeHeader ? "bg-white sticky top-0 z-10" : "lg:hidden bg-white sticky top-0 z-10"}>
            <tr className="h-20 border-b border-border">
              <th className="text-left px-4 py-2 text-sm font-medium text-foreground bg-white" style={{width: '200px'}}>Product</th>
              {visibleCountries.map(country => (
                <th key={country.code} className="text-center px-1 py-2 font-medium text-foreground bg-white relative group cursor-default select-none" style={{width: columnWidth}}>
                  <div className="flex flex-col items-center justify-center h-full">
                    {visibleCountries.length >= 14 ? (
                      <span className="text-xs select-none whitespace-nowrap transform -rotate-45 origin-center">{country.code}</span>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-full gap-1">
                        <span className="text-sm select-none">{country.flag}</span>
                        <span className="text-xs select-none whitespace-nowrap">{country.code}</span>
                      </div>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedProducts.map((product, index) => {
              const isHighlighted = highlightedProduct && product.solution === highlightedProduct;
              return (
            <tr
              key={index}
              className={`border-b border-border hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:shadow-sm cursor-pointer group transition-colors ${
                isHighlighted ? 'bg-yellow-100 dark:bg-yellow-900/30 animate-pulse' : ''
              }`}
              data-product-name={product.solution}
              onClick={() => onProductClick(product)}
            >
              <td className="p-4" style={{width: '200px'}}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="font-medium text-foreground text-sm leading-tight">{product.solution}</div>
                    {product.notes && (
                      <div className="flex-shrink-0" title="Additional details available">
                        <Info className="w-4 h-4 text-blue-500" />
                      </div>
                    )}
                  </div>
                  <div className="opacity-0 group-hover:opacity-100  flex items-center gap-1 text-blue-600 text-xs font-medium">
                    <span>View</span>
                    <ChevronRight className="w-3 h-3" />
                  </div>
                </div>
              </td>
              {/* No Status column - removed from all tables */}
              {/* Show country availability - only visible countries */}
              {visibleCountries.map(country => {
                const value = product.countries[country.code];
                // Special case: For Supported Payments tab on SkyTab/SkyTab Venue, show blank for Spain and Australia
                const isSupportedPaymentsTab = platformName === 'Supported Payments';
                const isSkyTabPlatform = selectedPlatform === 'SkyTab' || selectedPlatform === 'SkyTab Venue';
                const isSpainOrAustralia = country.code === 'Spain' || country.code === 'Australia';
                const shouldShowBlank = isSupportedPaymentsTab && isSkyTabPlatform && isSpainOrAustralia;

                return (
                  <td
                    key={country.code}
                    className="p-4 text-center"
                    style={{width: columnWidth}}
                    data-tooltip={
                      shouldShowBlank
                        ? `${product.solution} - Not applicable in ${country.name}`
                        : value === true
                        ? `${product.solution} available in ${country.name}`
                        : value === 'Coming Soon'
                        ? `${product.solution} coming soon to ${country.name}`
                        : `${product.solution} not available in ${country.name}`
                    }
                    data-tooltip-position={index === 0 ? "bottom" : undefined}
                  >
                    {shouldShowBlank ? (
                      <span className="text-transparent">-</span>
                    ) : value === true ? (
                      <span className="text-green-600 font-semibold">✓</span>
                    ) : value === 'Coming Soon' ? (
                      <span className="text-transparent">-</span>
                    ) : (
                      <span className="text-red-500 font-semibold">✗</span>
                    )}
                  </td>
                );
              })}
                </tr>
              );
            })}
            </tbody>
          </table>
      </div>
    </>
  );
};
